/** Organisation DETAIL — the shapes behind Fabric's 35 `companies` routes.
 *
 *  STAGE A: these are FABRIC'S SHAPES, deliberately unchanged. Spaces,
 *  allocations, filename-identified files and the archive lifecycle are all
 *  ported as they are, because porting and redesigning at once means a failure
 *  has two possible causes. The gate (see TODO_FABRIC.md) is applied in stage C,
 *  against a working system — not here.
 *
 *  So: nothing in this file is an endorsement of the model. `CompanyFile` has a
 *  filename and no hash because Fabric's does; `Allocation` carries a space
 *  because Fabric's does. Both are on the stage-C list.
 */

/** A (company, product, space) allocation — Fabric's `CompanyProductSpace`.
 *  `space` is what `uds_enter` scopes a login to. */
export type Allocation = {
  cps_id: number;
  product: string;
  product_slug: string;
  space: string;
  space_slug: string;
  /** A space defined for this company alone, rather than a global default. */
  private: boolean;
  enabled: boolean;
  users_with_access: number;
};

/** Per-(company, product, space) file library. Identity is the FILENAME —
 *  there is no hash and no version. Uniqueness covers ARCHIVED rows too, so an
 *  archived `firmware.bin` blocks re-upload of that name until it is revived or
 *  permanently deleted (deliberate: it stops silent shadowing). */
export type CompanyFile = {
  file_id: number;
  cps_id: number;
  kind: "firmware" | "config";
  filename: string;
  size_bytes: number;
  uploaded_by: string;
  uploaded_at: string;
  archived: boolean;
};

/** One of three fixed reference slots per product, overridden for this company.
 *  Resolution is company-first, default-fallback. "Reset override" archives
 *  EVERY active override row for the slot at once, so the resolver falls back. */
export type ReferenceOverride = {
  ref_file_id: number;
  cps_id: number;
  ref_type: "app_config_did" | "mfg_config_did" | "data_upload_signals";
  filename: string;
  /** false = this company is using the product-wide default. */
  overridden: boolean;
  archived: boolean;
  uploaded_at: string | null;
};

/** Which tool build each of this company's machines is running. Exported to
 *  Excel from Fabric's `tool-versions/export.xlsx`. */
export type ToolVersionRow = {
  login_id: string;
  tool: string;
  platform: string;
  version_name: string;
  version_code: number;
  last_seen: string;
  /** Below the rollout floor — this machine must update. */
  below_floor: boolean;
};

export const REF_TYPE_LABEL: Record<ReferenceOverride["ref_type"], string> = {
  app_config_did: "App Config DID",
  mfg_config_did: "Mfg Config DID",
  data_upload_signals: "Historic data signals",
};

// ---------------------------------------------------------------- the data --

export const ALLOCATIONS: Allocation[] = [
  { cps_id: 11, product: "Connected Cluster", product_slug: "connected_cluster",
    space: "EOL", space_slug: "eol", private: false, enabled: true, users_with_access: 3 },
  { cps_id: 12, product: "Connected Cluster", product_slug: "connected_cluster",
    space: "R&D testing", space_slug: "rnd_testing", private: false, enabled: true, users_with_access: 1 },
  { cps_id: 13, product: "Connected Cluster", product_slug: "connected_cluster",
    space: "Nexcell line 2", space_slug: "nexcell_line_2", private: true, enabled: false, users_with_access: 0 },
  { cps_id: 21, product: "Warranty Insights", product_slug: "warranty_insights",
    space: "Default", space_slug: "default", private: false, enabled: true, users_with_access: 2 },
];

export const COMPANY_FILES: CompanyFile[] = [
  { file_id: 101, cps_id: 11, kind: "firmware", filename: "cluster_app.bin",
    size_bytes: 421_888, uploaded_by: "ravi@aphelion.in", uploaded_at: "2026-09-18", archived: false },
  { file_id: 102, cps_id: 11, kind: "firmware", filename: "cluster_bootloader.bin",
    size_bytes: 61_440, uploaded_by: "ravi@aphelion.in", uploaded_at: "2026-09-18", archived: false },
  { file_id: 103, cps_id: 11, kind: "config", filename: "cluster_app_config.xlsx",
    size_bytes: 18_944, uploaded_by: "ravi@aphelion.in", uploaded_at: "2026-09-18", archived: false },
  { file_id: 104, cps_id: 11, kind: "firmware", filename: "cluster_app.bin",
    size_bytes: 419_112, uploaded_by: "ravi@aphelion.in", uploaded_at: "2026-08-02", archived: true },
  { file_id: 121, cps_id: 12, kind: "firmware", filename: "cm_v18_torque_test.bin",
    size_bytes: 425_984, uploaded_by: "krisha.chhadawa@aphelion.in", uploaded_at: "2026-09-21", archived: false },
];

export const REFERENCE_OVERRIDES: ReferenceOverride[] = [
  { ref_file_id: 0, cps_id: 11, ref_type: "app_config_did", filename: "app_config_did.xlsx",
    overridden: false, archived: false, uploaded_at: null },
  { ref_file_id: 501, cps_id: 11, ref_type: "mfg_config_did", filename: "mfg_config_nexcell.xlsx",
    overridden: true, archived: false, uploaded_at: "2026-09-12" },
  { ref_file_id: 0, cps_id: 11, ref_type: "data_upload_signals", filename: "historic_signals.desc",
    overridden: false, archived: false, uploaded_at: null },
];

export const TOOL_VERSIONS: ToolVersionRow[] = [
  { login_id: "farah@nexcell.in", tool: "UDS tool", platform: "windows",
    version_name: "2.4.1", version_code: 241, last_seen: "2026-09-25 09:12", below_floor: false },
  { login_id: "sameer@nexcell.in", tool: "UDS tool", platform: "windows",
    version_name: "2.4.1", version_code: 241, last_seen: "2026-09-25 08:40", below_floor: false },
  { login_id: "vikram@nexcell.in", tool: "UDS tool", platform: "windows",
    version_name: "2.2.0", version_code: 220, last_seen: "2026-09-19 17:03", below_floor: true },
  { login_id: "anita@nexcell.in", tool: "UDS tool", platform: "windows",
    version_name: "2.4.1", version_code: 241, last_seen: "2026-09-24 11:55", below_floor: false },
];

export function useAllocations(_orgId: string): Allocation[] { return ALLOCATIONS; }
export function useCompanyFiles(cpsId: number): CompanyFile[] {
  return COMPANY_FILES.filter((f) => f.cps_id === cpsId);
}
export function useReferenceOverrides(cpsId: number): ReferenceOverride[] {
  return REFERENCE_OVERRIDES.filter((r) => r.cps_id === cpsId);
}
export function useToolVersions(_orgId: string): ToolVersionRow[] { return TOOL_VERSIONS; }
